<template>
  <v-container fluid class="gradient-background" style="min-height: 100vh;">
    <v-row justify="center" class="pt-10">
      <v-col cols="12" md="6" class="text-center">
        <v-avatar size="120" class="elevation-6">
          <v-img :src="member.image || defaultImage" />
        </v-avatar>
        <h2 class="mt-4 font-weight-bold" style="color: white;">{{ member.fullName }}</h2>
      </v-col>
    </v-row>

    <!-- About Section -->
    <v-row justify="center">
      <v-col cols="12" md="8">
        <h3 class="section-title" style="color: white;">About</h3>
        <v-card class="pa-4" outlined>
          <p>{{ member.description || "No description available." }}</p>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <!-- Posts if connected -->
    <template v-if="isConnected">
      <v-divider class="my-6"></v-divider>
      <v-row justify="center">
        <v-col cols="12" md="8">
          <h3 class="section-title" style="color: white;">Posts</h3>
          <v-row>
            <v-col v-if="posts.length === 0" cols="12">
              <p>No posts available.</p>
            </v-col>
            <v-col
              v-for="post in posts"
              :key="post.id"
              cols="12"
              sm="6"
              lg="4"
            >
              <v-hover v-slot="{ hover }">
                <v-card
                  :elevation="hover ? 10 : 2"
                  class="mb-6 transition-swing"
                  style="background-color: white; color: white; cursor: pointer;"
                  rounded="lg"
                  @click="$router.push({ name: 'PostDetail', params: { postId: post.id } })"
                >
                  <v-responsive :aspect-ratio="16/9">
                    <v-img v-if="post.imageURL" :src="post.imageURL" cover />
                    <video
                      v-else-if="post.videoURL"
                      controls
                      :src="post.videoURL"
                      style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px;"
                    />
                  </v-responsive>
                  <v-card-text class="pt-3 px-4">
                    <p class="text-subtitle-1" style="color: black;">{{ post.text }}</p>
                  </v-card-text>
                  <v-card-actions class="px-4 pb-3 pt-1 d-flex justify-end">
                    <v-icon color="red">mdi-heart-outline</v-icon>
                    <v-icon color="blue" class="ml-4">mdi-comment-outline</v-icon>
                  </v-card-actions>
                </v-card>
              </v-hover>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>

    <!-- Connect button if not connected -->
<!-- Connect button if not connected and not viewing self -->
<v-row justify="center" v-else-if="!isSelfProfile">
  <v-col cols="auto" class="text-center mt-6">
    <v-btn color="primary" text rounded @click="connectWithUser(member.id)">
      Connect With
    </v-btn>
  </v-col>
</v-row>


    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :timeout="3000" color="info">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { db } from "@/firebase";
import { doc, getDoc, collection, getDocs, writeBatch } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

export default {
  props: ["id", "uid"],
  data() {
    return {
      member: {},
      posts: [],
      isConnected: false,
      isSelfProfile: false, // <-- NEW FLAG
      defaultImage: "https://via.placeholder.com/120",
      snackbar: {
        show: false,
        message: "",
      },
      currentUser: null,
    };
  },
  methods: {
    async checkConnection() {
      const auth = getAuth();
      const currentUID = auth.currentUser?.uid;

      if (!currentUID || !this.uid) {
        this.isConnected = false;
        this.posts = [];
        return;
      }

      this.isSelfProfile = currentUID === this.uid;

      if (this.isSelfProfile) {
        this.isConnected = true;
        await this.fetchPosts();
        return;
      }

      try {
        const connDoc = await getDoc(doc(db, `user/${currentUID}/connections/${this.uid}`));
        this.isConnected = connDoc.exists();

        if (this.isConnected) {
          await this.fetchPosts();
        } else {
          this.posts = [];
        }
      } catch (err) {
        console.error("Error checking connection:", err);
        this.isConnected = false;
        this.posts = [];
      }
    },

    async connectWithUser(targetUID) {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        this.showSnackbar("You must be signed in to connect.");
        return;
      }

      if (user.uid === targetUID) {
        this.showSnackbar("You can't connect with yourself.");
        return;
      }

      try {
        const [mySnap, theirSnap] = await Promise.all([
          getDoc(doc(db, "user", user.uid)),
          getDoc(doc(db, "user", targetUID)),
        ]);

        if (!mySnap.exists() || !theirSnap.exists()) {
          throw new Error("User data not found.");
        }

        const myData = mySnap.data();
        const theirData = theirSnap.data();

        const myConnRef = doc(db, `user/${user.uid}/connections/${targetUID}`);
        const theirConnRef = doc(db, `user/${targetUID}/connections/${user.uid}`);

        const timestamp = new Date();

        const myConnectionData = {
          uid: targetUID,
          fullName: theirData.fullName || "",
          email: theirData.email || "",
          image: theirData.image || "",
          gender: theirData.gender || "",
          groups: theirData.groups || [],
          isConnected: true,
          connectedAt: timestamp,
        };

        const theirConnectionData = {
          uid: user.uid,
          fullName: myData.fullName || "",
          email: myData.email || "",
          image: myData.image || "",
          gender: myData.gender || "",
          groups: myData.groups || [],
          isConnected: true,
          connectedAt: timestamp,
        };

        const batch = writeBatch(db);
        batch.set(myConnRef, myConnectionData);
        batch.set(theirConnRef, theirConnectionData);
        await batch.commit();

        this.showSnackbar("Connection established!");

        this.isConnected = true;
        await this.fetchPosts?.();
      } catch (err) {
        console.error("Connection error:", err);
        this.showSnackbar("Failed to connect: " + err.message);
      }
    },

    async fetchMemberDetails() {
      try {
        const userDoc = await getDoc(doc(db, "user", this.id));
        this.member = userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : {};
      } catch (err) {
        console.error("Error fetching member:", err);
      }
    },

    async fetchPosts() {
      try {
        const snapshot = await getDocs(collection(db, "explore"));
        this.posts = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(post => post.uid === this.uid);
      } catch (err) {
        console.error("Error fetching posts:", err);
        this.posts = [];
      }
    },

    showSnackbar(message) {
      this.snackbar.message = message;
      this.snackbar.show = true;
    },

    async initView() {
      if (!this.id) this.id = this.$route.params.id || this.uid;
      if (!this.uid) this.uid = this.$route.params.uid || this.id;

      await this.fetchMemberDetails();
      await this.checkConnection();
    },
  },

  async mounted() {
    const auth = getAuth();
    onAuthStateChanged(auth, user => {
      this.currentUser = user;
      this.initView();
    });

    await this.initView();
  },

  beforeRouteEnter(to, from, next) {
    next(vm => vm.initView());
  },

  beforeRouteUpdate(to, from, next) {
    this.initView().then(() => next());
  },
};
</script>


<style scoped>
.section-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1rem;
}
.gradient-background {
  background: linear-gradient(to bottom right, #f2c46d, #3b5d58);
}
.v-card:hover {
  transform: translateY(-4px);
  transition: transform 0.2s ease;
}
</style>
